import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { QUICK_PICKUPS } from '../../../data/quickPickupLocations';
import { COLORS, SHADOWS } from '../../../styles/tokens';
import AddressInput from '../../../components/common/AddressInput';
import SupplierLogo from '../../../components/common/SupplierLogo';
import { uploadFile } from '../../../firebase/storageService';
import { validateLogoFile, validateLogoUrl, logoPath } from '../../../utils/supplierLogo';
import SupplierOffersField from './SupplierOffersField';
import { useMassCatalog } from '../../../utils/massCatalog';
import { hasOffers, offersSubtitle, validateOffers } from '../../../utils/supplierOffers';

// ── Firestore ref ──────────────────────────────────────────────────────────

const SETTINGS_REF = () => doc(db, 'settings', 'quickPickups');

// ── Helpers ────────────────────────────────────────────────────────────────

async function persistLocations(locations) {
  await setDoc(SETTINGS_REF(), { locations }, { merge: true });
}

// ── Constants ──────────────────────────────────────────────────────────────

const EMPTY_FORM = { icon: '', name: '', subtitle: '', street: '', city: '', lat: null, lng: null, offers: {}, logoUrl: '', kind: 'supplier' };

// ── Main component ─────────────────────────────────────────────────────────

export default function AdminSuppliers() {
  const catalog = useMassCatalog();
  const [locations,   setLocations]   = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [editingId,   setEditingId]   = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [saveStatus,  setSaveStatus]  = useState(null); // null | 'saving' | 'ok' | 'err'
  const [saveError,   setSaveError]   = useState('');
  const [saveScope,   setSaveScope]   = useState('global'); // 'global' | 'form'
  const [uploading,   setUploading]   = useState(false);
  const [addKey,      setAddKey]      = useState(0); // ny nøkkel per «Legg til» så skjemaet remountes
  const saveStatusTimerRef = useRef(null);
  const isReorderingRef = useRef(false);

  function setStatus(status, error = '', scope = 'global') {
    clearTimeout(saveStatusTimerRef.current);
    setSaveStatus(status);
    setSaveError(error);
    setSaveScope(scope);
    if (status === 'ok') {
      saveStatusTimerRef.current = setTimeout(() => setSaveStatus(null), 2000);
    }
  }

  // Load on mount
  useEffect(() => {
    let cancelled = false;
    getDoc(SETTINGS_REF())
      .then(snap => {
        if (cancelled) return;
        const data = snap.data();
        setLocations(data?.locations?.length > 0 ? data.locations : QUICK_PICKUPS);
      })
      .catch(() => { if (!cancelled) setLocations(QUICK_PICKUPS); })
      .finally(() => { if (!cancelled) setPageLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sensors = useSensors(useSensor(PointerSensor));

  // Drag-and-drop: optimistic reorder with rollback
  const handleDragEnd = useCallback(async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    if (isReorderingRef.current) return;
    isReorderingRef.current = true;
    const snapshot  = locations;
    const oldIndex  = snapshot.findIndex(l => l.id === active.id);
    const newIndex  = snapshot.findIndex(l => l.id === over.id);
    const reordered = arrayMove(snapshot, oldIndex, newIndex);
    setLocations(reordered);
    try {
      await persistLocations(reordered);
    } catch {
      setLocations(snapshot);
      setStatus('err', 'Kunne ikke lagre rekkefølge — prøv igjen.');
    } finally {
      isReorderingRef.current = false;
    }
  }, [locations]);

  // Form helpers
  function openAdd() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setStatus(null);
    setAddKey(k => k + 1);
    setShowAddForm(true);
  }

  function openEdit(loc) {
    setShowAddForm(false);
    setForm({ icon: loc.icon, name: loc.name, subtitle: loc.subtitle,
              street: loc.street, city: loc.city, lat: loc.lat, lng: loc.lng, offers: loc.offers ?? {}, logoUrl: loc.logoUrl ?? '',
              kind: loc.kind === 'receiver' ? 'receiver' : 'supplier' });
    setStatus(null);
    setEditingId(loc.id);
  }

  function cancelForm() {
    setShowAddForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setStatus(null);
  }

  // Save (add or edit): validate coordinates → write Firestore → update local state
  async function handleSave(e) {
    e.preventDefault();
    if (uploading) {
      setStatus('err', 'Vent til opplastingen er ferdig', 'form');
      return;
    }
    setStatus('saving', '', 'form');

    if (form.lat == null || form.lng == null) {
      setStatus('err', 'Velg adresse fra listen for å få koordinater', 'form');
      return;
    }

    const offersError = validateOffers(form.offers, catalog);
    if (offersError) {
      setStatus('err', offersError, 'form');
      return;
    }

    const logoError = validateLogoUrl(form.logoUrl);
    if (logoError) {
      setStatus('err', logoError, 'form');
      return;
    }

    // offers lagres bare når sortimentet er registrert; undertittelen genereres da fra sortimentet
    const { offers, ...rest } = form;
    const registered = Object.keys(offers ?? {}).length > 0;
    const data = { ...rest, subtitle: (registered && offersSubtitle(offers, catalog)) || rest.subtitle };
    const logoUrl = (form.logoUrl ?? '').trim();
    const build = (base) => {
      const { offers: _old, logoUrl: _oldLogo, ...clean } = base;
      return { ...clean, ...(registered ? { offers } : {}), ...(logoUrl ? { logoUrl } : {}) };
    };

    const updated = editingId
      ? locations.map(l => l.id === editingId ? build({ ...l, ...data }) : l)
      : [...locations, build({ id: crypto.randomUUID(), ...data })];

    try {
      await persistLocations(updated);
    } catch {
      setStatus('err', 'Lagring til Firestore feilet — prøv igjen.', 'form');
      return;
    }

    setLocations(updated);
    setShowAddForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setStatus('ok');
  }

  // Delete: optimistic remove with rollback
  async function handleDelete(loc) {
    if (!window.confirm(`Slett ${loc.name}?`)) return;
    const snapshot = locations;
    const updated  = snapshot.filter(l => l.id !== loc.id);
    setLocations(updated);
    try {
      await persistLocations(updated);
    } catch {
      setLocations(snapshot);
      setStatus('err', 'Sletting feilet — prøv igjen.');
    }
  }

  if (pageLoading) {
    return <div style={styles.page}><p style={{ color: COLORS.textMid, marginTop: 32 }}>Laster…</p></div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.title}>🏗️ Masseleverandører</h2>
        <button className="tippi-btn" style={styles.addBtn} onClick={openAdd}>+ Legg til</button>
      </div>

      {saveStatus === 'ok'  && <div style={styles.successBanner}>Lagret!</div>}
      {saveStatus === 'err' && saveScope === 'global' && (
        <div style={styles.errorBanner}>{saveError}</div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={locations.map(l => l.id)} strategy={verticalListSortingStrategy}>
          <div style={styles.list}>
            {locations.map(loc => (
              <React.Fragment key={loc.id}>
                <SupplierRow
                  loc={loc}
                  onEdit={() => openEdit(loc)}
                  onDelete={() => handleDelete(loc)}
                />
                {editingId === loc.id && (
                  <SupplierForm
                    key={`edit-${loc.id}`}
                    uploading={uploading}
                    onUploadingChange={setUploading}
                    form={form}
                    setForm={setForm}
                    onSave={handleSave}
                    onCancel={cancelForm}
                    saveStatus={saveStatus}
                    saveError={saveError}
                    supplierId={editingId}
                    isEdit
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {showAddForm && (
        <SupplierForm
          key={`add-${addKey}`}
          uploading={uploading}
          onUploadingChange={setUploading}
          form={form}
          setForm={setForm}
          onSave={handleSave}
          onCancel={cancelForm}
          saveStatus={saveStatus}
          saveError={saveError}
          supplierId={null}
          isEdit={false}
        />
      )}
    </div>
  );
}

// ── SupplierRow ────────────────────────────────────────────────────────────

function SupplierRow({ loc, onEdit, onDelete }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: loc.id });

  const style = {
    ...styles.row,
    transform:  CSS.Transform.toString(transform),
    transition,
    opacity:    isDragging ? 0.5 : 1,
    boxShadow:  isDragging ? SHADOWS.md : styles.row.boxShadow,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div {...attributes} {...listeners} style={styles.dragHandle} title="Dra for å endre rekkefølge">
        ⠿
      </div>
      <div style={styles.rowIcon}>
        <SupplierLogo logoUrl={loc.logoUrl} size={30} fallback={loc.icon} />
      </div>
      <div style={styles.rowInfo}>
        <div style={styles.rowName}>
          {loc.name}
          {!hasOffers(loc) && <span style={styles.badge}>Ikke registrert</span>}
          {loc.kind === 'receiver' && <span style={styles.badge}>Mottak</span>}
        </div>
        <div style={styles.rowSub}>{loc.subtitle}</div>
        <div style={styles.rowAddr}>{loc.street} · {loc.city}</div>
      </div>
      <div style={styles.rowActions}>
        <button type="button" style={styles.iconBtn} onClick={onEdit} title="Rediger">✏️</button>
        <button type="button" style={styles.iconBtn} onClick={onDelete} title="Slett">🗑</button>
      </div>
    </div>
  );
}

// ── SupplierForm ───────────────────────────────────────────────────────────

function SupplierForm({ form, setForm, onSave, onCancel, saveStatus, saveError, isEdit, supplierId, uploading, onUploadingChange }) {
  const catalog = useMassCatalog();
  const registered = Object.keys(form.offers ?? {}).length > 0;
  const [logoError, setLogoError] = useState('');
  const uploadToken = useRef(0);

  // Skjemaet fjernes/byttes: forkast pågående opplasting og nullstill opplastingsstatus
  useEffect(() => () => {
    uploadToken.current += 1;
    onUploadingChange(false);
  }, [onUploadingChange]);

  async function onFile(e) {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    const problem = validateLogoFile(file);
    if (problem) { setLogoError(problem); input.value = ''; return; }
    const token = ++uploadToken.current;
    setLogoError('');
    onUploadingChange(true);
    try {
      const url = await uploadFile(file, logoPath(supplierId, file));
      if (token !== uploadToken.current) return;
      setForm(f => ({ ...f, logoUrl: url }));
    } catch {
      if (token !== uploadToken.current) return;
      setLogoError('Opplasting feilet — prøv igjen.');
    }
    onUploadingChange(false);
    input.value = '';
  }

  return (
    <form onSubmit={onSave} style={styles.form}>
      <div style={styles.formRow}>
        <div style={{ flex: '0 0 80px' }}>
          <label style={styles.label}>Ikon</label>
          <input
            className="tippi-input"
            style={{ ...styles.input, textAlign: 'center', fontSize: 22 }}
            value={form.icon}
            onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
            maxLength={2}
            required
            placeholder="⛏️"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={styles.label}>Navn</label>
          <input
            className="tippi-input"
            style={styles.input}
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            required
            placeholder="Franzefoss Minerals"
          />
        </div>
      </div>
      <div>
        <label htmlFor="supplier-kind" style={styles.label}>Type</label>
        <select
          id="supplier-kind"
          className="tippi-input"
          style={styles.input}
          value={form.kind ?? 'supplier'}
          onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
        >
          <option value="supplier">Utsalg (masser hentes her)</option>
          <option value="receiver">Mottak (masser kjøres hit)</option>
        </select>
      </div>
      <div>
        <label style={styles.label}>Undertittel</label>
        <input
          className="tippi-input"
          style={styles.input}
          value={registered ? (offersSubtitle(form.offers, catalog) || form.subtitle) : form.subtitle}
          onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
          readOnly={registered}
          required={!registered}
          placeholder="Pukk, grus, sand"
        />
        {registered && <div style={styles.hintText}>Genereres fra sortimentet under.</div>}
      </div>
      <SupplierOffersField
        value={form.offers}
        kind={form.kind}
        onChange={offers => setForm(f => ({ ...f, offers }))}
      />
      <div>
        <label style={styles.label}>Logo</label>
        <div style={styles.logoRow}>
          <SupplierLogo logoUrl={form.logoUrl} size={40} fallback={<span style={styles.logoEmpty}>Ingen</span>} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              className="tippi-input"
              style={styles.input}
              aria-label="Logo-lenke"
              placeholder="https://… (lenke til logoen)"
              value={form.logoUrl}
              onChange={e => setForm(f => ({ ...f, logoUrl: e.target.value }))}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="file" aria-label="Last opp logo" disabled={uploading} accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onFile} />
              {form.logoUrl && (
                <button type="button" style={styles.linkBtn} onClick={() => setForm(f => ({ ...f, logoUrl: '' }))}>Fjern logo</button>
              )}
            </div>
            {uploading && <div style={styles.hintText}>Laster opp…</div>}
            {logoError && <div style={styles.logoError}>{logoError}</div>}
          </div>
        </div>
      </div>
      <AddressInput
        label="Adresse"
        value={{ street: form.street, city: form.city, lat: form.lat, lng: form.lng }}
        onChange={addr => setForm(f => ({ ...f, ...addr }))}
        required
      />

      {saveStatus === 'err' && saveError && (
        <div style={styles.formError}>{saveError}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="button" className="tippi-btn-secondary" style={styles.btnSecondary} onClick={onCancel}>
          Avbryt
        </button>
        <button type="submit" className="tippi-btn" style={styles.btnPrimary} disabled={saveStatus === 'saving' || uploading}>
          {saveStatus === 'saving' ? 'Lagrer…' : isEdit ? 'Lagre endringer' : 'Legg til'}
        </button>
      </div>
    </form>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  page:          { padding: '32px 24px', maxWidth: 680, background: COLORS.white, flex: 1, overflowY: 'auto' },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title:         { fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: 0 },
  addBtn:        { padding: '10px 18px', borderRadius: 8, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  list:          { display: 'flex', flexDirection: 'column', gap: 4 },
  row:           { display: 'flex', alignItems: 'flex-start', gap: 12, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  dragHandle:    { fontSize: 18, color: COLORS.textLight, cursor: 'grab', userSelect: 'none', paddingTop: 4, touchAction: 'none' },
  rowIcon:       { fontSize: 22, minWidth: 30, paddingTop: 2 },
  rowInfo:       { flex: 1, minWidth: 0 },
  rowName:       { fontSize: 14, fontWeight: 700, color: COLORS.textDark },
  rowSub:        { fontSize: 12, color: COLORS.textMid, marginTop: 1 },
  rowAddr:       { fontSize: 11, color: COLORS.textLight, marginTop: 2 },
  rowActions:    { display: 'flex', gap: 4, alignSelf: 'center' },
  iconBtn:       { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '4px 6px', borderRadius: 6 },
  form:          { background: COLORS.surfaceHover, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '16px', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 8 },
  formRow:       { display: 'flex', gap: 10 },
  label:         { fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 3, display: 'block' },
  input:         { padding: '9px 11px', borderRadius: 7, border: `1.5px solid ${COLORS.border}`, fontSize: 14, width: '100%', boxSizing: 'border-box', background: COLORS.white, color: COLORS.textDark },
  formError:     { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '9px 12px', borderRadius: 7, fontSize: 13 },
  btnPrimary:    { flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  btnSecondary:  { flex: 1, padding: '10px', borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, fontSize: 14, cursor: 'pointer', color: COLORS.textMid },
  badge:         { marginLeft: 8, fontSize: 11, fontWeight: 600, color: COLORS.textMid, background: COLORS.surfaceHover, border: `1px solid ${COLORS.border}`, borderRadius: 99, padding: '1px 8px' },
  hintText:      { fontSize: 11, color: COLORS.textLight, marginTop: 3 },
  logoRow:       { display: 'flex', gap: 12, alignItems: 'flex-start' },
  logoEmpty:     { fontSize: 11, color: COLORS.textLight, border: `1px dashed ${COLORS.border}`, borderRadius: 6, padding: '10px 6px' },
  logoError:     { fontSize: 12, color: COLORS.dangerText },
  linkBtn:     { background: 'none', border: 'none', color: COLORS.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 },
  successBanner: { background: COLORS.successBg, color: COLORS.successText, padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600 },
  errorBanner:   { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 },
};
